/**
 * Single source of truth for Sample Order pipeline stage labels, chip
 * variants, and allowed transitions.
 *
 * Mirrors the Python constants in `middleware/app/models/sample_order.py`.
 * Any drift between frontend and backend trips a unit test.
 */

import type { BlockedReason, PipelineType, Stage } from '../api/types'

type ChipVariant = 'neutral' | 'success' | 'warning' | 'danger' | 'info'

/** Canonical order. */
export const STAGES: readonly Stage[] = [
  'carla_created',
  'new_request',
  'blocked',
  'approved',
  'preparing',
  'partially_shipped',
  'shipped',
  'cancelled',
] as const

export const TERMINAL_STAGES: readonly Stage[] = ['shipped', 'cancelled'] as const

/**
 * Non-terminal stages — shown as board columns. Shipped/Cancelled archive
 * off the board immediately, the deliberate contrast with the Uviva Sample
 * Requests board (which keeps Shipped/Delivered/Follow-up visible).
 */
export const BOARD_STAGES: readonly Stage[] = [
  'carla_created',
  'new_request',
  'blocked',
  'approved',
  'preparing',
  'partially_shipped',
] as const

export const STAGE_LABELS: Record<Stage, string> = {
  carla_created: 'CARLA Created',
  new_request: 'New Request',
  blocked: 'Blocked',
  approved: 'Approved',
  preparing: 'Preparing',
  partially_shipped: 'Partially Shipped',
  shipped: 'Shipped',
  cancelled: 'Cancelled',
}

export const STAGE_VARIANT: Record<Stage, ChipVariant> = {
  carla_created: 'info',
  new_request: 'neutral',
  blocked: 'danger',
  approved: 'info',
  preparing: 'info',
  partially_shipped: 'warning',
  shipped: 'success',
  cancelled: 'danger',
}

export const BLOCKED_REASON_LABELS: Record<BlockedReason, string> = {
  out_of_stock: 'Out of stock',
  incomplete_info: 'Incomplete info',
  pending_approval: 'Pending approval',
  awaiting_customer_response: 'Awaiting customer response',
  other: 'Other',
}

export const BLOCKED_REASONS: readonly BlockedReason[] = [
  'out_of_stock',
  'incomplete_info',
  'pending_approval',
  'awaiting_customer_response',
  'other',
] as const

export const PIPELINE_TYPE_LABELS: Record<PipelineType, string> = {
  sample_team: 'Sample Team',
  spi_sales_team: 'SPI Sales Team',
}

export const PIPELINE_TYPES: readonly PipelineType[] = ['sample_team', 'spi_sales_team'] as const

// ─── Transitions (permissive kanban) ──────────────────────────────
// Mirrors ALLOWED_TRANSITIONS in the Python model:
//   Any non-terminal → any other stage
//   Terminals can only undo to `blocked`

type Transition = { from: Stage; to: Stage }

const _nonTerminals = STAGES.filter(
  (s) => !TERMINAL_STAGES.includes(s as (typeof TERMINAL_STAGES)[number]),
)

export const ALLOWED_TRANSITIONS: readonly Transition[] = [
  ..._nonTerminals.flatMap((from) =>
    STAGES.filter((to) => to !== from).map((to) => ({ from, to })),
  ),
  ...TERMINAL_STAGES.map((from) => ({ from, to: 'blocked' as Stage })),
]

export function transitionAllowed(from: Stage, to: Stage): boolean {
  if (from === to) return false
  return ALLOWED_TRANSITIONS.some((t) => t.from === from && t.to === to)
}

export function allowedTargetsFrom(stage: Stage): Stage[] {
  return ALLOWED_TRANSITIONS.filter((t) => t.from === stage).map((t) => t.to)
}

export function isTerminal(stage: Stage): boolean {
  return TERMINAL_STAGES.includes(stage as (typeof TERMINAL_STAGES)[number])
}
